import React, { useState } from 'react';
import { Box, Typography, CardContent, Paper, Stack, TextField, Button, Alert, CircularProgress } from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useLookupEncountersQuery } from '../../../services/EncounterService';
import { useCreateRefundMutation } from '../../../services/RefundService';
import { EncounterType } from '../../../services/EncounterService/types/encounterTypes';
import { EncounterSearchGrid } from '../components/grids';
import SectionPaper from '../../common/components/styled-ui-elements/SectionPaper';
import PageCard from '../../common/components/styled-ui-elements/PageCard';
import { CenterSelector } from '../../common/components';
import { useCenterSelection } from '../../common/hooks/useCenterSelection';

// Types
interface RefundCreateParams {
  company: string;
  center: string;
}

interface SearchState {
  term: string;
  isSearching: boolean;
  creatingRefundFor: string | null;
  createError: string | null;
}

// Constants
const MINIMUM_SEARCH_LENGTH = 3;

const THEME_COLORS = {
  primary: '#162346',
  primaryHover: '#1a2a52',
  background: '#f8fafc',
  card: '#ffffff',
  text: {
    primary: '#1e293b',
    secondary: 'text.secondary'
  }
} as const;

// Utility functions
const formatDateForApi = (dateString: string | undefined): string | null => {
  if (!dateString) return null;
  try {
    return new Date(dateString).toISOString().split('T')[0];
  } catch {
    return null;
  }
};

const buildRefundCreateRequest = (encounter: EncounterType, company: string, center: string) => {
  // Ensure we have a valid encounter ID
  if (!encounter.id) {
    throw new Error('Encounter ID is required');
  }

  return {
    caseId: encounter.id,
    company: company,
    centerId: center,
    accountNumber: encounter.caseIdentification?.accountNumber || '',
    firstName: encounter.patient?.firstName || '',
    lastName: encounter.patient?.lastName || '',
    visitKey: encounter.caseIdentification?.visitKey || '',
    serviceDate: formatDateForApi(encounter.encounterInfo?.serviceDate),
  };
};

const buildNavigationUrl = (refundId: string, company: string, center: string): string => {
  const params = new URLSearchParams();
  params.append('company', company);
  params.append('center', center);
  params.append('id', refundId);
  return `/refunds/edit?${params.toString()}`;
};

// Custom hook for managing refund creation parameters
const useRefundCreateParams = (): RefundCreateParams => {
  const [searchParams] = useSearchParams();
  
  return {
    company: searchParams.get('company') || '',
    center: searchParams.get('center') || ''
  };
};

// Custom hook for managing search state
const useSearchState = () => {
  const [searchState, setSearchState] = useState<SearchState>({
    term: '',
    isSearching: false,
    creatingRefundFor: null,
    createError: null
  });

  const updateSearchState = (updates: Partial<SearchState>) => {
    setSearchState(prev => ({ ...prev, ...updates }));
  };

  return { searchState, updateSearchState };
};

// Custom hook for refund creation logic
const useRefundCreation = () => {
  const navigate = useNavigate();
  const [createRefundMutation] = useCreateRefundMutation();

  const createRefund = async (
    encounter: EncounterType, 
    params: RefundCreateParams,
    onStateChange: (updates: Partial<SearchState>) => void
  ) => {
    // Validation
    if (!params.company || !params.center) {
      onStateChange({ createError: 'Company and center are required' });
      return;
    }

    // Start creation process
    onStateChange({ 
      creatingRefundFor: encounter.id, 
      createError: null 
    });

    try {
      const createRequest = buildRefundCreateRequest(encounter, params.company, params.center);
      const createdRefund = await createRefundMutation(createRequest).unwrap();
      
      // Navigate to edit page
      const navigationUrl = buildNavigationUrl(createdRefund.id, params.company, params.center);
      navigate(navigationUrl);
      
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to create refund request. Please try again.';
      onStateChange({ 
        createError: errorMessage,
        creatingRefundFor: null
      });
      console.error('Create refund error:', error);
    }
  };

  return { createRefund };
};

// Header Component
const PageHeader: React.FC<{ company: string; center: string }> = ({ company, center }) => (
  <Paper elevation={1} sx={{ p: 1.5, mb: 2 }}>
    <Stack direction="row" justifyContent="space-between" alignItems="center">
      <Box>
        <Typography variant="h6" component="h1" sx={{ mb: 0.25, fontWeight: 600 }}>
          Create Refund - Select Encounter
        </Typography>
        <Typography variant="body2" color={THEME_COLORS.text.secondary} sx={{ fontSize: '0.85rem' }}>
          Search for encounters and select one to create a refund request (will auto-create and redirect to edit)
          {company && center && (
            <span> | Company: {company} | Center: {center}</span>
          )}
        </Typography>
      </Box>
    </Stack>
  </Paper>
);

// Search Form Component
const SearchForm: React.FC<{
  searchTerm: string;
  isSearching: boolean;
  onSearchChange: (value: string) => void;
  onSearchSubmit: (event: React.FormEvent) => void;
}> = ({ searchTerm, isSearching, onSearchChange, onSearchSubmit }) => (
  <SectionPaper sx={{ mb: 3 }}>
    <Typography variant="subtitle1" sx={{ fontWeight: 600, color: THEME_COLORS.text.primary, mb: 2 }}>
      Search Encounters
    </Typography>
    <form onSubmit={onSearchSubmit}>
      <Stack direction="row" spacing={2} alignItems="flex-start">
        <TextField
          label="Search"
          placeholder="Enter MRN, patient name, account number, or visit key"
          value={searchTerm}
          onChange={(e) => onSearchChange(e.target.value)}
          size="small"
          fullWidth
          helperText={`Enter at least ${MINIMUM_SEARCH_LENGTH} characters to search`}
        />
        <Button
          type="submit"
          variant="contained"
          startIcon={isSearching ? <CircularProgress size={16} color="inherit" /> : <SearchIcon />}
          disabled={searchTerm.length < MINIMUM_SEARCH_LENGTH || isSearching}
          sx={{
            backgroundColor: THEME_COLORS.primary,
            '&:hover': {
              backgroundColor: THEME_COLORS.primaryHover,
            },
            fontWeight: 600,
            minWidth: 120,
            mt: 0.5
          }}
        >
          {isSearching ? 'Searching...' : 'Search'}
        </Button>
      </Stack>
    </form>
  </SectionPaper>
);

// Results Section Component
const ResultsSection: React.FC<{
  searchTerm: string;
  encounters: EncounterType[];
  isLoadingSearch: boolean;
  searchError: any;
  createError: string | null;
  creatingRefundFor: string | null;
  onSelectEncounter: (encounter: EncounterType) => void;
}> = ({ 
  searchTerm, 
  encounters, 
  isLoadingSearch, 
  searchError, 
  createError, 
  creatingRefundFor, 
  onSelectEncounter 
}) => (
  <>
    {/* Error Messages */}
    {createError && (
      <Alert severity="error" sx={{ mb: 3 }}>
        {createError}
      </Alert>
    )}

    {searchError && (
      <Alert severity="error" sx={{ mb: 3 }}>
        Error searching encounters. Please try again.
      </Alert>
    )}

    {/* No Results Message */}
    {searchTerm.length >= MINIMUM_SEARCH_LENGTH && !isLoadingSearch && encounters.length === 0 && (
      <Alert severity="info" sx={{ mb: 3 }}>
        No encounters found for your search criteria.
      </Alert>
    )}

    {/* Search Results Grid */}
    {encounters.length > 0 && (
      <EncounterSearchGrid
        encounters={encounters}
        onSelectEncounter={onSelectEncounter}
        creatingRefundFor={creatingRefundFor}
        isLoading={isLoadingSearch}
      />
    )}
  </>
);

// Main Component
export const RefundCreate: React.FC = () => {
  const params = useRefundCreateParams();
  const { searchState, updateSearchState } = useSearchState();
  const { createRefund } = useRefundCreation();
  const { handleCenterChange } = useCenterSelection();
  
  // API hooks
  const { 
    data: encounters = [], 
    error: searchError, 
    isLoading: isLoadingSearch,
    refetch
  } = useLookupEncountersQuery(
    { 
      company: params.company, 
      center: params.center, 
      searchTerm: searchState.term 
    }, 
    { skip: !searchState.term.trim() || searchState.term.length < MINIMUM_SEARCH_LENGTH }
  );

  // Event handlers
  const handleSearchChange = (value: string) => {
    updateSearchState({ term: value });
  };

  const handleSearchSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (searchState.term.trim().length >= MINIMUM_SEARCH_LENGTH) {
      updateSearchState({ isSearching: true });
      try {
        await refetch();
      } finally {
        updateSearchState({ isSearching: false });
      }
    }
  };

  const handleSelectEncounter = async (encounter: EncounterType) => {
    await createRefund(encounter, params, updateSearchState);
  };

  return (
    <Box sx={{ 
      minHeight: '100vh',
      backgroundColor: THEME_COLORS.background,
      py: 1.5,
      px: 2
    }}>
      <Box display="flex" justifyContent="center">
        <PageCard>
          <PageHeader company={params.company} center={params.center} />
          
          <CardContent sx={{ p: 3, backgroundColor: THEME_COLORS.card }}>
            <Box sx={{ mb: 3 }}>
              <CenterSelector value={params.center} onChange={handleCenterChange} />
            </Box>
            <SearchForm
              searchTerm={searchState.term}
              isSearching={searchState.isSearching}
              onSearchChange={handleSearchChange}
              onSearchSubmit={handleSearchSubmit}
            />

            <ResultsSection
              searchTerm={searchState.term}
              encounters={encounters}
              isLoadingSearch={isLoadingSearch}
              searchError={searchError}
              createError={searchState.createError}
              creatingRefundFor={searchState.creatingRefundFor}
              onSelectEncounter={handleSelectEncounter}
            />
          </CardContent>
        </PageCard>
      </Box>
    </Box>
  );
};
